import { FunctionComponent } from "react";
import { NavLink, useLocation } from "react-router-dom";

import { classNames } from "@/utils/classNames";
import { SidenavItem } from "@/constants/sidenav.config";

import { isNavPathActive } from "../Sidebar/filterNavItems";
import { Tooltip } from "../Tooltip/Tooltip";

interface SidenavItemProps extends SidenavItem {
	children?: JSX.Element | JSX.Element[];
	testId?: string;
	isSidebarOpen?: boolean;
	count?: number;
	bubbleColor?: "red" | "yellow" | "green" | "orange";
	bubbleText?: React.ReactNode;
	explanationText?: string;
}

const BUBBLE_BG: Record<string, string> = {
	red: "bg-status-error",
	yellow: "bg-status-warning",
	green: "bg-status-success",
	orange: "bg-status-review",
};

export const SidebarItem: FunctionComponent<SidenavItemProps> = ({
	bubbleColor,
	bubbleText,
	title,
	path,
	strictPath,
	icon,
	iconActive,
	onClick,
	children,
	isBeta,
	testId,
	type,
	isSidebarOpen,
	count,
	explanationText = "",
	dataOnboarding,
}) => {
	const location = useLocation();

	// Segment-boundary match shared with SidebarGroup — see isNavPathActive.
	const isActive = isNavPathActive(location.pathname, { path, strictPath });

	const hasCount = !!count && +count > 0;
	const countLabel = count === 100 ? "99+" : count;
	const bubbleBg = bubbleColor ? BUBBLE_BG[bubbleColor] : undefined;
	const showBubble = !!bubbleColor && !!bubbleText;

	const renderIcon = () => {
		const currentIcon = isActive && iconActive ? iconActive : icon;

		if (typeof currentIcon === "string") {
			return (
				<img
					alt="icon"
					className={classNames("w-[24px] rounded", type === "logo" ? "bg-gray-700" : "")}
					src={currentIcon}
				/>
			);
		}

		return currentIcon;
	};

	const rowClassName = classNames(
		"group relative flex items-center h-9 rounded-[9px] text-sm font-medium mb-[1px] cursor-pointer transition-colors",
		isSidebarOpen ? "gap-[11px] px-[10px] w-full" : "justify-center w-11 mx-auto",
		isActive
			? "bg-sidebar-item-active text-brand-light font-semibold"
			: "text-text-meta hover:bg-sidebar-item-hover hover:text-text"
	);

	const iconWrapClassName = classNames(
		"relative flex items-center justify-center flex-shrink-0",
		isActive ? "text-brand-light" : "text-placeholder group-hover:text-text-meta"
	);

	// The row's descriptive part (icon + title + count). Kept separate from the
	// status dot so the general-explanation tooltip can wrap ONLY this part —
	// nesting the dot's own status tooltip inside the explanation tooltip made
	// hovering the dot fire the explanation instead of the status (PREC-3584).
	const rowMain = (
		<>
			{isActive && isSidebarOpen && (
				<span className="absolute -left-[10px] top-[7px] bottom-[7px] w-[3px] rounded-r-[3px] bg-brand-light" />
			)}
			<span className={iconWrapClassName}>
				{renderIcon()}
				{!isSidebarOpen && (hasCount || showBubble) && (
					<span
						className={classNames(
							"absolute -top-[3px] -right-[4px] w-[7px] h-[7px] rounded-full border-[1.5px] border-brand-white",
							showBubble ? bubbleBg : "bg-brand-light"
						)}
					/>
				)}
			</span>
			{isSidebarOpen && (
				<span className="flex flex-1 items-center min-w-0 whitespace-nowrap">
					<span className="truncate">{title}</span>
					{isBeta && (
						<span className="ml-1 flex-shrink-0 text-xxs font-bold text-white bg-brand-light px-1.5 py-0.5 rounded-md">
							Beta
						</span>
					)}
				</span>
			)}
			{isSidebarOpen && hasCount && (
				<span className="ml-auto flex-shrink-0 text-[11px] font-semibold leading-none px-[7px] py-[3px] rounded-full bg-sidebar-item-active text-brand-light">
					{countLabel}
				</span>
			)}
		</>
	);

	// The status dot with its own tooltip (lead-scoring status). Rendered as a
	// sibling of the explanation tooltip — never nested inside it.
	const bubble = isSidebarOpen && showBubble && (
		<Tooltip content={bubbleText} className="ml-auto flex-shrink-0">
			<span className={classNames("w-2.5 h-2.5 rounded-full", bubbleBg)} />
		</Tooltip>
	);

	const inner = (
		<>
			{rowMain}
			{bubble}
		</>
	);

	const renderContent = () => {
		if (!isSidebarOpen) {
			return (
				<Tooltip
					className="flex w-full items-center justify-center"
					content={title}
					direction="top-right"
					variant="dark"
				>
					{inner}
				</Tooltip>
			);
		}

		if (explanationText.length > 0) {
			return (
				<div className="flex flex-1 items-center gap-[11px]">
					<Tooltip
						className="flex flex-1 items-center gap-[11px]"
						content={
							<>
								<h5 className="font-bold mb-2">{title}</h5>
								<p>{explanationText}</p>
							</>
						}
					>
						{rowMain}
					</Tooltip>
					{bubble}
				</div>
			);
		}

		return inner;
	};

	return (
		<NavLink
			className={rowClassName}
			data-onboarding={dataOnboarding}
			data-test={testId}
			to={path || ""}
			onClick={onClick}
		>
			{renderContent()}
			{children && <div>{children}</div>}
		</NavLink>
	);
};
